import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';

export interface TabItem {
  id: string;
  label: string;
  count?: number;
}

interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange?: (id: string) => void;
  style?: ViewStyle;
}

export default function Tabs({ tabs, value, onChange, style }: TabsProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={[
        {
          flexGrow: 0,
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
        },
        style,
      ]}
      contentContainerStyle={{ flexDirection: 'row' }}
      accessibilityRole="tablist"
    >
      {tabs.map((tab) => {
        const isActive = value === tab.id;

        const tabStyle: ViewStyle = {
          paddingVertical: 9,
          paddingHorizontal: 14,
          borderBottomWidth: 2,
          borderBottomColor: isActive ? colors.ink : 'transparent',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          ...Platform.select({
            web: {
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'border-color 100ms, color 100ms',
            } as ViewStyle,
            default: {},
          }),
        };

        const labelStyle: TextStyle = {
          fontSize: fontSize.md,
          fontWeight: fontWeight.medium,
          color: isActive ? colors.ink : colors.ink3,
          fontFamily: theme.font.sans,
          includeFontPadding: false,
        };

        const countStyle: TextStyle = {
          fontSize: fontSize.xs,
          fontFamily: theme.font.mono,
          color: colors.ink3,
          includeFontPadding: false,
        };

        return (
          <TouchableOpacity
            key={tab.id}
            style={tabStyle}
            onPress={() => onChange?.(tab.id)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            activeOpacity={0.7}
          >
            <Text style={labelStyle}>{tab.label}</Text>
            {tab.count !== undefined && (
              <Text style={countStyle}>{tab.count}</Text>
            )}
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}
